import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './Analytics.css';

interface DailyStat {
  date: string;
  totalTracks: number;
  uniqueTracks: number;
}

interface StoreAnalytics {
  storeName: string;
  storeRef: string;
  dailyStats: DailyStat[];
}

interface AnalyticsData {
  [storeRef: string]: StoreAnalytics;
}

const ITEMS_PER_PAGE = 10;

// Helper function to format numbers with thousand separators
const formatNumber = (num: number): string => {
  return num.toLocaleString('en-US');
};

// Helper function to export data to CSV
const exportToCSV = (data: StoreAnalytics) => {
  const headers = ['תאריך', 'יום בשבוע', 'מעקבים ייחודיים', 'סה"כ מעקבים', 'יחס (%)'];
  const rows = data.dailyStats.map(stat => {
    const date = new Date(stat.date);
    const dayName = date.toLocaleDateString('he-IL', { weekday: 'long' });
    const dateStr = date.toLocaleDateString('he-IL');
    const ratio = stat.totalTracks > 0 
      ? ((stat.uniqueTracks / stat.totalTracks) * 100).toFixed(0) 
      : '0';
    
    return [dateStr, dayName, stat.uniqueTracks, stat.totalTracks, ratio];
  });

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');

  // Add BOM for Hebrew support
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `analytics_${data.storeRef}_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const Analytics: React.FC = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPages, setCurrentPages] = useState<{ [storeRef: string]: number }>({});
  const [selectedStore, setSelectedStore] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [selectedPreset, setSelectedPreset] = useState('all');

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('/api/admin/analytics');
      if (response.data.success) {
        setAnalytics(response.data.data);
        
        // Initialize pagination for all stores
        const initialPages: { [key: string]: number } = {};
        Object.keys(response.data.data).forEach(storeRef => {
          initialPages[storeRef] = 1;
        });
        setCurrentPages(initialPages);

        // Select first store by default if available
        const stores = Object.keys(response.data.data);
        if (stores.length > 0 && !selectedStore) {
          setSelectedStore(stores[0]);
        }
      }
    } catch (err: any) {
      console.error('Error fetching analytics:', err);
      setError(err.response?.data?.message || 'שגיאה בטעינת נתונים אנליטיים');
    } finally {
      setLoading(false);
    }
  }, [selectedStore]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handlePageChange = (storeRef: string, newPage: number) => {
    setCurrentPages(prev => ({
      ...prev,
      [storeRef]: newPage
    }));
  };

  const getPaginatedData = (storeRef: string, data: DailyStat[]) => {
    const currentPage = currentPages[storeRef] || 1;
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return data.slice(startIndex, endIndex);
  };

  const getTotalPages = (dataLength: number) => {
    return Math.ceil(dataLength / ITEMS_PER_PAGE);
  };

  const calculateTotals = (stats: DailyStat[]) => {
    return stats.reduce(
      (acc, stat) => ({
        totalTracks: acc.totalTracks + stat.totalTracks,
        uniqueTracks: acc.uniqueTracks + stat.uniqueTracks,
      }),
      { totalTracks: 0, uniqueTracks: 0 }
    );
  };

  const filterByDateRange = (stats: DailyStat[]) => {
    if (!dateRange.from && !dateRange.to) return stats;
    
    return stats.filter(stat => {
      // Compare date strings directly (YYYY-MM-DD format)
      if (dateRange.from && stat.date < dateRange.from) return false;
      if (dateRange.to && stat.date > dateRange.to) return false;
      return true;
    });
  };

  const filteredStores = Object.values(analytics).filter(store =>
    store.storeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    store.storeRef.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleExport = () => {
    if (selectedStore && analytics[selectedStore]) {
      const filteredData = {
        ...analytics[selectedStore],
        dailyStats: filterByDateRange(analytics[selectedStore].dailyStats)
      };
      exportToCSV(filteredData);
    }
  };

  const handleBackToAdmin = () => {
    window.location.href = '/admin';
  };

  const setDatePreset = (days: number | 'today' | 'all') => {
    if (days === 'all') {
      setDateRange({ from: '', to: '' });
      return;
    }
    
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const todayStr = `${year}-${month}-${day}`;
    
    if (days === 'today') {
      setDateRange({ from: todayStr, to: todayStr });
    } else {
      const fromDate = new Date(today);
      fromDate.setDate(fromDate.getDate() - days + 1); // Include today in the range
      const fromYear = fromDate.getFullYear();
      const fromMonth = String(fromDate.getMonth() + 1).padStart(2, '0');
      const fromDay = String(fromDate.getDate()).padStart(2, '0');
      const fromDateStr = `${fromYear}-${fromMonth}-${fromDay}`;
      setDateRange({ from: fromDateStr, to: todayStr });
    }
  };

  if (loading) {
    return (
      <div className="analytics-page">
        <div className="analytics-loading">
          <h2>⏳ טוען נתונים אנליטיים...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-page">
        <div className="analytics-error">
          <h2>❌ שגיאה</h2>
          <p>{error}</p>
          <button onClick={fetchAnalytics} className="btn-retry">
            נסה שוב
          </button>
        </div>
      </div>
    );
  }

  if (Object.keys(analytics).length === 0) {
    return (
      <div className="analytics-page">
        <div className="analytics-empty">
          <h2>📊 אין נתונים זמינים</h2>
          <p>לא נמצאו נתוני מעקב ב-30 הימים האחרונים</p>
          <button onClick={fetchAnalytics} className="btn-refresh">
            רענן נתונים
          </button>
        </div>
      </div>
    );
  }

  const selectedStoreData = selectedStore ? analytics[selectedStore] : null;
  const filteredSelectedData = selectedStoreData ? {
    ...selectedStoreData,
    dailyStats: filterByDateRange(selectedStoreData.dailyStats)
  } : null;

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <div className="analytics-title">
          <h1>📊 אנליטיקס</h1>
          <p className="analytics-subtitle">נתוני מעקב לכל התקופה</p>
        </div>
        <div className="header-actions">
          <button onClick={handleBackToAdmin} className="btn-back">
            ← חזור לניהול
          </button>
          <button onClick={fetchAnalytics} className="btn-refresh">
            🔄 רענן נתונים
          </button>
        </div>
      </div>

      <div className="analytics-layout">
        {/* Sidebar with store list */}
        <div className="analytics-sidebar">
          <h3>חנויות ({filteredStores.length})</h3>
          
          {/* Search box */}
          <div className="search-box">
            <input
              type="text"
              className="search-input"
              placeholder="🔍 חפש חנות..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="store-list">
            {filteredStores.map((store) => {
              const totals = calculateTotals(store.dailyStats);
              const isSelected = selectedStore === store.storeRef;
              
              return (
                <div
                  key={store.storeRef}
                  className={`store-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => setSelectedStore(store.storeRef)}
                >
                  <div className="store-item-header">
                    <h4>{store.storeName}</h4>
                    <span className="store-ref-badge">{store.storeRef}</span>
                  </div>
                  <div className="store-item-stats">
                    <div className="stat-mini">
                      <span className="stat-label">סה"כ מעקבים:</span>
                      <span className="stat-value">{formatNumber(totals.totalTracks)}</span>
                    </div>
                    <div className="stat-mini">
                      <span className="stat-label">מעקבים ייחודיים:</span>
                      <span className="stat-value">{formatNumber(totals.uniqueTracks)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Main content area */}
        <div className="analytics-content">
          {filteredSelectedData ? (
            <>
              <div className="store-analytics-header">
                <div>
                  <h2>{filteredSelectedData.storeName}</h2>
                  <p className="store-ref">מזהה: {filteredSelectedData.storeRef}</p>
                </div>
              </div>

              {/* Toolbar with filters and export */}
              <div className="analytics-toolbar">
                <div className="toolbar-left">
                  <div className="date-filter-row">
                    {/* Date Preset Dropdown */}
                    <div className="preset-dropdown-container">
                      <label>תקופה:</label>
                      <select 
                        className="preset-dropdown"
                        value={selectedPreset}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSelectedPreset(value);
                          if (value === 'all') {
                            setDatePreset('all');
                          } else if (value === 'today') {
                            setDatePreset('today');
                          } else {
                            setDatePreset(parseInt(value));
                          }
                        }}
                      >
                        <option value="all">כל התקופה</option>
                        <option value="today">היום</option>
                        <option value="3">3 ימים אחרונים</option>
                        <option value="7">שבוע אחרון</option>
                        <option value="14">שבועיים אחרונים</option>
                        <option value="30">חודש אחרון</option>
                      </select>
                    </div>
                    
                    {/* Custom Date Range */}
                    <div className="custom-date-range">
                      <label>או בחר תאריך:</label>
                      <input
                        type="date"
                        value={dateRange.from}
                        onChange={(e) => {
                          setDateRange({ ...dateRange, from: e.target.value });
                          setSelectedPreset('');
                        }}
                        placeholder="מתאריך"
                      />
                      <span className="date-separator">עד</span>
                      <input
                        type="date"
                        value={dateRange.to}
                        onChange={(e) => {
                          setDateRange({ ...dateRange, to: e.target.value });
                          setSelectedPreset('');
                        }}
                        placeholder="עד תאריך"
                      />
                    </div>
                  </div>
                </div>
                <div className="toolbar-right">
                  <button onClick={handleExport} className="btn-export">
                    📥 ייצא ל-CSV
                  </button>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="summary-cards">
                {(() => {
                  const totals = calculateTotals(filteredSelectedData.dailyStats);
                  const avgPerDay = filteredSelectedData.dailyStats.length > 0
                    ? (totals.totalTracks / filteredSelectedData.dailyStats.length).toFixed(1)
                    : '0';
                  
                  return (
                    <>
                      <div className="summary-card">
                        <div className="summary-icon">📦</div>
                        <div className="summary-content">
                          <div className="summary-value">{formatNumber(totals.totalTracks)}</div>
                          <div className="summary-label">סה"כ מעקבים</div>
                        </div>
                      </div>
                      <div className="summary-card">
                        <div className="summary-icon">👥</div>
                        <div className="summary-content">
                          <div className="summary-value">{formatNumber(totals.uniqueTracks)}</div>
                          <div className="summary-label">מעקבים ייחודיים</div>
                        </div>
                      </div>
                      <div className="summary-card">
                        <div className="summary-icon">📈</div>
                        <div className="summary-content">
                          <div className="summary-value">{avgPerDay}</div>
                          <div className="summary-label">ממוצע ליום</div>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>

              {filteredSelectedData.dailyStats.length === 0 ? (
                <div className="no-data-message">
                  <p>אין נתונים זמינים לחנות זו</p>
                </div>
              ) : (
                <>
                  <div className="analytics-table-container">
                    <table className="analytics-table">
                      <thead>
                        <tr>
                          <th>תאריך</th>
                          <th>יום בשבוע</th>
                          <th>מעקבים ייחודיים</th>
                          <th>סה"כ מעקבים</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getPaginatedData(filteredSelectedData.storeRef, filteredSelectedData.dailyStats).map((stat) => {
                          const date = new Date(stat.date);
                          const dayName = date.toLocaleDateString('he-IL', { weekday: 'long' });
                          const dateStr = date.toLocaleDateString('he-IL', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          });

                          return (
                            <tr key={stat.date}>
                              <td className="date-cell">{dateStr}</td>
                              <td className="day-cell">{dayName}</td>
                              <td className="unique-cell">{formatNumber(stat.uniqueTracks)}</td>
                              <td className="total-cell">{formatNumber(stat.totalTracks)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {getTotalPages(filteredSelectedData.dailyStats.length) > 1 && (
                    <div className="pagination">
                      <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(
                          filteredSelectedData.storeRef,
                          (currentPages[filteredSelectedData.storeRef] || 1) - 1
                        )}
                        disabled={(currentPages[filteredSelectedData.storeRef] || 1) === 1}
                      >
                        ← הקודם
                      </button>
                      
                      <div className="pagination-info">
                        עמוד {currentPages[filteredSelectedData.storeRef] || 1} מתוך{' '}
                        {getTotalPages(filteredSelectedData.dailyStats.length)}
                        {' '}({formatNumber(filteredSelectedData.dailyStats.length)} רשומות)
                      </div>
                      
                      <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(
                          filteredSelectedData.storeRef,
                          (currentPages[filteredSelectedData.storeRef] || 1) + 1
                        )}
                        disabled={
                          (currentPages[filteredSelectedData.storeRef] || 1) >=
                          getTotalPages(filteredSelectedData.dailyStats.length)
                        }
                      >
                        הבא →
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <div className="select-store-message">
              <p>בחר חנות מהרשימה כדי לראות את הנתונים</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Analytics;

